<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePlatformFeePreview } from '@/composables/usePlatformFeePreview'
import { formatCentsAsCurrency, formatPercentage } from '@/utils/currencyFormat'
import { normalisePreviewAmount } from '@/utils/platformFeeAmount'

const props = defineProps<{
  organisationId: number
  price: string
}>()

const { t, locale } = useI18n()
const { preview, loading, error } = usePlatformFeePreview(
  () => props.organisationId,
  () => props.price,
)

const visible = computed<boolean>(() => normalisePreviewAmount(props.price) !== null)

const rateText = computed<string>(() => {
  if (preview.value === null) return ''
  const { percentage_basis_points: bps, fixed_cents: fixed } = preview.value.rate
  const percentage = formatPercentage(bps / 100, locale.value)
  const fixedText = formatCentsAsCurrency(fixed, preview.value.currency, locale.value)
  if (bps === 0 && fixed === 0) return t('races.form.fee_preview.rate_none')
  if (fixed === 0) return percentage
  if (bps === 0) return fixedText
  return t('races.form.fee_preview.rate', { percentage, fixed: fixedText })
})

function money(cents: number): string {
  return formatCentsAsCurrency(cents, preview.value?.currency ?? 'eur', locale.value)
}
</script>

<template>
  <div
    v-if="visible"
    class="rounded-lg border border-slate-700 bg-slate-900/40 px-4 py-3 text-sm"
    aria-live="polite"
    data-testid="fee-preview"
  >
    <p v-if="error" class="text-slate-400">{{ t('races.form.fee_preview.unavailable') }}</p>
    <p v-else-if="preview === null" class="text-slate-400">{{ t('races.form.fee_preview.loading') }}</p>
    <template v-else>
      <div class="space-y-1" :class="{ 'opacity-60': loading }">
        <div class="flex items-center justify-between gap-4 text-slate-300">
          <span>{{ t('races.form.fee_preview.platform_fee', { rate: rateText }) }}</span>
          <span>- {{ money(preview.platform_fee_cents) }}</span>
        </div>
        <div class="flex items-center justify-between gap-4 font-semibold text-white">
          <span>{{ t('races.form.fee_preview.net') }}</span>
          <span>{{ money(preview.net_amount_cents) }}</span>
        </div>
      </div>
      <p class="mt-2 text-xs text-slate-400">
        {{ t('races.form.fee_preview.deducted_note') }}
        {{ t('races.form.fee_preview.options_vouchers_hint') }}
      </p>
    </template>
  </div>
</template>
